import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { IoIosStar } from "react-icons/io";
import { FaClock, FaMapMarkerAlt, FaPhone, FaWhatsapp } from "react-icons/fa";
import { MdDone, MdArrowBack } from "react-icons/md";
import { GoHeartFill, GoHeart } from "react-icons/go";
import config from "../../../config";

import axios from "axios";
import ServiceCard from "../../components/ServiceCard";

const VendorDetail = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Get vendor data from navigation state or fetch from API
  useEffect(() => {
    const vendorFromState = location.state?.vendor;
       
    if (vendorFromState) {
      
      setVendor(vendorFromState);
      setLoading(false);
    } else {
    
      fetchVendorDetails();
    }
  }, [vendorId, location.state]);

  const fetchVendorDetails = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${config.API_BASE_URL}/vendors/getvendor/${vendorId}`
      );

      

      if (response.data.status) {
        setVendor(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch vendor details");
      }
    } catch (error) {
      console.error("Error fetching vendor details:", error);
      setError(
        error.response?.data?.message ||
          "Failed to load vendor details. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = (id) => {
    if (!id) return;
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((favId) => favId !== id) : [...prev, id]
    );
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleGetLocation = () => {
    // Check for location in address object
    if (vendor?.address?.officeAddress?.location?.coordinates) {
      const [longitude, latitude] =
        vendor.address.officeAddress.location.coordinates;
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, "_blank");
    } else if (vendor?.address?.homeAddress?.location?.coordinates) {
      const [longitude, latitude] =
        vendor.address.homeAddress.location.coordinates;
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, "_blank");
    }
  };

  const handleCall = () => {
    // Check for phone in vendor object or nested vendor_id
    const phone = vendor?.phone || vendor?.vendor_id?.phone;
    if (phone) {
      window.open(`tel:${phone}`, "_self");
    }
  };

  const handleWhatsApp = () => {
    // Check for phone in vendor object or nested vendor_id
    const phone = vendor?.phone || vendor?.vendor_id?.phone;
    const name = vendor?.name || vendor?.vendor_id?.name || "this vendor";
    if (phone) {
      const message = `Hi, I'm interested in your services at ${name}`;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6216]"></div>
          <p className="mt-4 text-gray-600">Loading vendor details...</p>
        </div>
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <div className="min-h-screen bg-[#FFFCFC] flex items-center justify-center">
        <div className="text-center">
          <div className="p-6 bg-red-100 border border-red-400 text-red-700 rounded max-w-md">
            <p className="font-semibold">Error loading vendor details</p>
            <p className="text-sm mt-2">{error || "Vendor not found"}</p>
            <button
              onClick={handleBackClick}
              className="mt-4 bg-[#FF6216] text-white px-6 py-2 rounded hover:bg-[#e05514]"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Get vendor name from either direct property or nested vendor_id
  const vendorName =
    vendor.profile.name || vendor.vendor_id?.name || "Unnamed Vendor";

  // Get about text from either direct property or nested vendor_id
  const aboutText =
    vendor.profile.about ||
    vendor.vendor_id?.about ||
    "No description available";
  const fullText = aboutText;
  const words = fullText.split(" ");
  const previewText = words.slice(0, 70).join(" ");

  // Get address information
  const address = vendor.address || vendor.vendor_id?.address || {};
  const locality =
    address.locality || address.officeAddress?.locality || "Unknown";
  const city = address.city || address.officeAddress?.city || "Unknown";
  const fullAddress =
    address.full || address.officeAddress?.full || `${locality}, ${city}`;
  const pincode = address.pincode || address.officeAddress?.pincode;

  // Get phone number
  const phone = vendor.phone || vendor.vendor_id?.phone;

  // Get profile photo
  const profilePhoto =
    vendor.photo.profilePhoto ||
    vendor.vendor_id?.profilePhoto ||
    "/img/dogLogo.png";

  // Get shop images
  const shopImages = vendor.photo.shop || vendor.photo.serviceArea || [];
  // Get services
  const services = vendor.services || vendor.vendor_id?.services || [];

  // Get business hours
  const businessHours =
    vendor.businessHours || vendor.vendor_id?.businessHours || [];

  // Get rating and review info
  const rating =
    vendor.start_rating || vendor.vendor_id?.start_rating || "No Rating";
  const totalReviews =
    vendor.total_review || vendor.vendor_id?.total_review || "0";
  const isVerified = vendor.varified || vendor.vendor_id?.varified || 0;

  // Get status, time, price, available (these might be from the listing API)
  const status = vendor.status || "Status not available";
  const time = vendor.time || "Hours not available";
  const price = vendor.price || "Price not available";
  const available = vendor.available || "Availability not available";

  // Get features/amenities
  const features =
    vendor.features ||
    vendor.ameneties ||
    vendor.vendor_id?.features ||
    vendor.vendor_id?.ameneties ||
    [];

  return (
    <div className="min-h-screen bg-[#FFFCFC]">
      {/* Header with back button */}
      <div className="sticky top-0 bg-white shadow-sm z-10">
        <div className="px-4 py-3 flex items-center gap-4">
          <button
            onClick={handleBackClick}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <MdArrowBack className="w-6 h-6 text-[#000048]" />
          </button>
          <h1 className="text-lg font-semibold text-[#000048]">
            Vendor Details
          </h1>
        </div>
      </div>

      <div className="px-4 md:px-14 py-6 md:py-12">
        {/* Header */}
        <div className="relative">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-[#000048] text-2xl md:text-[36px] font-medium">
                {vendorName}
              </p>
              <p className="text-[#000048] text-base md:text-[19px] font-medium">
                {locality}, {city}
              </p>

              <div className="flex items-center flex-wrap gap-3 py-3">
                {[1, 2, 3, 4].map((_, idx) => (
                  <IoIosStar
                    key={idx}
                    className="bg-red-600 w-5 h-5 md:w-6 md:h-6 rounded-full text-white"
                  />
                ))}
                <p className="text-[#000048] text-lg md:text-[20px]">
                  {rating}
                </p>
                <p className="text-[#000048] text-lg md:text-[20px]">
                  ( {totalReviews} reviews )
                </p>
                <p className="text-[#000048] text-lg md:text-[20px] flex items-center gap-2">
                  <FaClock className="w-4 h-4 md:w-5 md:h-5" /> {time}
                </p>
              </div>

              <p className="text-[#000048] text-lg md:text-[20px] flex items-center gap-2">
                <MdDone className="w-5 h-5 md:w-6 md:h-6 bg-blue-700 text-white rounded-full" />
                {isVerified ? "Verified" : "Not Verified"}
              </p>
            </div>

            {/* Favorite button */}
            <div className="ml-4">
              <button
                onClick={() => toggleFavorite(vendor._id)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                {favorites.includes(vendor._id) ? (
                  <GoHeartFill className="text-red-500 text-2xl" />
                ) : (
                  <GoHeart className="text-gray-400 text-2xl" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Images + Address */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mt-8 relative">
          {/* Images */}
          <div className="flex-1">
            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-1 w-full lg:flex lg:flex-row">
              {Array.isArray(vendor?.photo?.shop) &&
              vendor.photo.shop.length > 0 ? (
                vendor.photo.shop.map((image, idx) => (
                  <img
                    key={idx}
                    src={`${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${image}`}
                    alt={`Shop ${idx + 1}`}
                    className="w-full h-32 md:h-40 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/img/building.png"; // fallback image
                    }}
                  />
                ))
              ) : (
                <p className="text-gray-400 text-sm col-span-2">
                  No shop images available.
                </p>
              )}
            </div>
          </div>

          {/* Profile Image */}
          <div className="flex justify-center mb-6 absolute top-16 left-10 z-10">
            <img
              src={profilePhoto}
              alt="Profile"
              className="w-20 h-20 md:w-28 md:h-28 rounded-full border-[4px] md:border-[7px] border-[#FFFAEB] object-cover"
              onError={(e) => {
                e.target.src = "/img/dogLogo.png";
              }}
            />
          </div>

          {/* Address */}
          <div className="border border-[#FD590133] rounded-xl p-4 md:p-5 w-full lg:w-[320px]">
            <div className="flex items-center gap-3 mb-4 md:mb-5">
              <FaMapMarkerAlt className="w-5 h-5 md:w-6 md:h-6 text-[#FF6216]" />
              <p className="text-[#000048] text-lg md:text-[20px] font-medium">
                Address
              </p>
            </div>
            <p className="text-[#000048] text-sm md:text-[17px] leading-[1.5]">
              {fullAddress}
            </p>
            {pincode && (
              <p className="text-[#000048] text-sm md:text-[17px]">{pincode}</p>
            )}

            <div className="space-y-3 mt-4">
              <button
                onClick={handleGetLocation}
                className="text-[#FF6216] text-sm md:text-base hover:underline"
              >
                Get Location
              </button>

              {phone && (
                <div className="flex items-center gap-2">
                  <FaPhone className="w-4 h-4 text-[#FF6216]" />
                  <button
                    onClick={handleCall}
                    className="text-[#000048] text-sm md:text-base hover:underline"
                  >
                    {phone}
                  </button>
                </div>
              )}

              {phone && (
                <div className="flex items-center gap-2">
                  <FaWhatsapp className="w-4 h-4 text-green-500" />
                  <button
                    onClick={handleWhatsApp}
                    className="text-[#000048] text-sm md:text-base hover:underline"
                  >
                    WhatsApp
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="border border-[#FD590133] p-4 my-8">
          <p className="text-[#000048] text-sm md:text-[17px] leading-[1.5]">
            {isExpanded ? (
              fullText
            ) : (
              <>
                {previewText}...
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-[#FD5901] font-medium ml-1"
                >
                  Read more
                </button>
              </>
            )}
          </p>
          {isExpanded && words.length > 70 && (
            <button
              onClick={() => setIsExpanded(false)}
              className="text-[#FD5901] font-medium mt-2"
            >
              Read less
            </button>
          )}
        </div>

        {/* Features/Amenities */}
        {features.length > 0 && (
          <div className="border border-[#FD590133] p-4 my-8">
            <h3 className="text-[#000048] text-lg md:text-xl font-medium mb-4">
              Features & Amenities
            </h3>
            <div className="flex flex-wrap gap-3">
              {features.map((feature, index) => (
                <span
                  key={index}
                  className="px-3 py-2 rounded-md text-[#000048] border border-[#FD590133] text-sm bg-[#FFFAEB]"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Service Card */}
        <ServiceCard services={services} vendor={vendor} />
      </div>
    </div>
  );
};

export default VendorDetail;
